/**
 * Admin film moderation / conversion phrasing (pure).
 * Chat never silent-writes — deep-link + guide only.
 */

export type SlateModerationKind =
    | 'ready_queue'
    | 'approve_advice'
    | 'reject_advice'
    | 'convert_advice';

export interface SlateModerationAsk {
    kind: SlateModerationKind;
    /** Film title when named in the ask */
    title?: string;
}

function cleanTitle(raw: string | undefined): string | undefined {
    if (!raw) return undefined;
    let t = raw
        .replace(/[?.!,]+$/g, '')
        .replace(/\b(?:the\s+)?(?:film|movie)\b/gi, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    t = t.replace(/^["']|["']$/g, '').trim();
    if (t.length < 2) return undefined;
    if (
        /^(this|that|it|one|film|movie|please|now|here)$/i.test(t)
    ) {
        return undefined;
    }
    return t;
}

/** “ready for approve / awaiting approval / submitted for review” → queue list */
export function isReadyForApproveAsk(text: string): boolean {
    const t = text.toLowerCase();
    return (
        /\bready\s+(?:for|to)\s+approv/.test(t) ||
        /\b(?:awaiting|waiting\s+for)\s+approv/.test(t) ||
        /\b(?:films?|movies?)\s+(?:to\s+)?(?:approve|review)\b/.test(t) ||
        /\bwhich\s+(?:films?|movies?).{0,40}\bapprov/.test(t) ||
        /\bneed(?:s)?\s+approv/.test(t) ||
        /\bpending\s+approv/.test(t)
    );
}

/**
 * Named-title moderation / conversion asks.
 * Examples:
 *  - Should I approve R patel?
 *  - Approve "R patel" movie
 *  - Reject film Dune
 *  - Start conversion for R patel
 */
export function extractModerationAsk(text: string): SlateModerationAsk | null {
    const raw = text.trim();
    if (!raw) return null;
    const t = raw.toLowerCase();

    if (isReadyForApproveAsk(raw) && !/\bshould\s+i\s+approv/.test(t)) {
        // Named title inside ready-queue ask → still queue (list), not single advice
        if (!/\b(?:should\s+i|can\s+i|do\s+i)\s+approv/.test(t)) {
            return { kind: 'ready_queue' };
        }
    }

    // Convert first (more specific verbs)
    const convert =
        raw.match(
            /\b(?:start\s+)?(?:conversion|convert)\s+(?:for\s+|of\s+|on\s+)?(?:the\s+)?(?:film|movie)?\s*["']?([^"'?]+?)["']?\s*$/i
        ) ||
        raw.match(
            /\b(?:start\s+)?(?:conversion|convert)\s+(?:for\s+|of\s+|on\s+)(.+?)(?:\?|$)/i
        ) ||
        raw.match(
            /\bhow\s+(?:do\s+i|to)\s+(?:start\s+)?(?:conversion|convert)\b/i
        );
    if (/\b(?:start\s+)?conversion\b|\bconvert\s+(?:film|movie|this|the)\b|\bconvert\s+["']/i.test(t)) {
        if (/\bhow\s+(?:do\s+i|to)\b/.test(t) && !cleanTitle(convert?.[1])) {
            return { kind: 'convert_advice' };
        }
        const title = cleanTitle(convert?.[1]);
        return { kind: 'convert_advice', title };
    }

    const approve =
        raw.match(
            /\b(?:should\s+i|can\s+i|do\s+i)\s+approv(?:e|al)?\s+(?:the\s+)?(?:film|movie)?\s*["']?([^"'?]+?)["']?\s*(?:film|movie)?\s*[?.!]?\s*$/i
        ) ||
        raw.match(
            /\bapprov(?:e|al)\s+(?:the\s+)?(?:film|movie)\s+["']?([^"'?]+?)["']?\s*[?.!]?\s*$/i
        ) ||
        raw.match(
            /\bapprov(?:e|al)\s+["']([^"']+)["']/i
        ) ||
        raw.match(
            /\bhow\s+(?:do\s+i|to)\s+approv(?:e|al)?\b/i
        );
    if (
        /\bapprov(?:e|al|ing)\b/.test(t) &&
        !/\bapproved\s+films?\b/.test(t) &&
        !isReadyForApproveAsk(raw)
    ) {
        if (/\bhow\s+(?:do\s+i|to)\b/.test(t) && !cleanTitle(approve?.[1])) {
            return { kind: 'approve_advice' };
        }
        if (
            /\b(?:should\s+i|can\s+i|do\s+i)\s+approv/.test(t) ||
            /\bapprov(?:e|al)\s+(?:the\s+)?(?:film|movie|["'])/.test(t)
        ) {
            return {
                kind: 'approve_advice',
                title: cleanTitle(approve?.[1]),
            };
        }
    }

    const reject =
        raw.match(
            /\b(?:should\s+i|can\s+i|do\s+i)\s+(?:reject|decline)\s+(?:the\s+)?(?:film|movie)?\s*["']?([^"'?]+?)["']?\s*(?:film|movie)?\s*[?.!]?\s*$/i
        ) ||
        raw.match(
            /\b(?:reject|decline)\s+(?:the\s+)?(?:film|movie)\s+["']?([^"'?]+)["']?/i
        ) ||
        raw.match(/\b(?:reject|decline)\s+["']([^"']+)["']/i) ||
        raw.match(/\b(?:reject|decline)\s+film\s+(.+?)(?:\?|$)/i) ||
        raw.match(/\bhow\s+(?:do\s+i|to)\s+(?:reject|decline)\b/i);
    if (/\b(?:reject|decline)\b/.test(t)) {
        if (/\bhow\s+(?:do\s+i|to)\b/.test(t) && !cleanTitle(reject?.[1])) {
            return { kind: 'reject_advice' };
        }
        if (
            /\b(?:should\s+i|can\s+i|do\s+i)\s+(?:reject|decline)/.test(t) ||
            /\b(?:reject|decline)\s+(?:the\s+)?(?:film|movie|["'])/.test(t) ||
            /\b(?:reject|decline)\s+film\b/.test(t)
        ) {
            return {
                kind: 'reject_advice',
                title: cleanTitle(reject?.[1]),
            };
        }
    }

    if (isReadyForApproveAsk(raw)) {
        return { kind: 'ready_queue' };
    }

    return null;
}
